import { useRowSelect } from '@table-library/react-table-library/select';
import { Action, State } from '@table-library/react-table-library/types/common';
import { Button, Card } from 'components/atoms';
import { FormInput, ViewToggleButton } from 'components/molecules';
import LabeledStatus from 'components/molecules/labeledStatus/LabeledStatus';
import ListWrapper from 'components/molecules/list-wrapper/ListWrapper';
import { QueryFilter, TopBar } from 'components/organisms';
import QueryFilterLabels from 'components/organisms/filters/query-form/labels/QueryFilterLabels';
import { TopbarPage } from 'components/templates';
import { useScroll } from 'components/templates/application/ApplicationTemplate';
import { useComponentVisible } from 'hooks';
import { useListViewById } from 'hooks/useListViewById';
import { useTableColumnsVisibility } from 'hooks/useTableColumnsVisibility';
import { useStoreState } from 'providers/StoreStateContext';
import { useTranslation } from 'react-i18next';
import { PiList, PiSquaresFour } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { useGetMerchListInfinite } from 'services/merch';
import { IMerchResponse, IMerchSearch } from 'services/merch/types';
import { STATUS } from 'services/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { useEnumToSelectOptions } from 'utils/components/inputs/select';
import { getNodes } from 'utils/table/nodes.utils';

enum MERCH_COLUMNS {
  name,
  ean,
  status,
}

const merchSearchInitialValues: IMerchSearch = {
  EAN: '',
  name: '',
  status: undefined,
};

const Merch = () => {
  const columnsVisibility = useTableColumnsVisibility('MerchList1', MERCH_COLUMNS);
  const viewId = 'merch';

  const { searchById, setSearchById } = useStoreState();
  const filters = searchById[columnsVisibility.tableId] || merchSearchInitialValues;

  const { view } = useListViewById(viewId, 'grid');

  const { data, isLoading, fetchNextPage, isFetchingNextPage } = useGetMerchListInfinite(filters);
  const scroll = useScroll({ fetchNextPage: fetchNextPage });
  const tableData = getNodes<IMerchResponse>(data?.pages.flat());

  const { t } = useTranslation();
  const navigate = useNavigate();
  const i18nextPrefix = 'merch';

  const { ref, isComponentVisible, handleComponentVisible } =
    useComponentVisible<HTMLDivElement>(false);

  const onSelectChange = (action: Action, state: State) => {
    navigate(`${state.id ?? state.ids[state.ids.length - 1]}/images`, {
      relative: 'path',
    });
  };

  const select = useRowSelect(tableData, {
    onChange: onSelectChange,
  });

  const { getSelectOptions } = useEnumToSelectOptions();
  return (
    <TopbarPage
      topbarAbsolute
      topbar={
        <TopBar
          title={t(`${i18nextPrefix}.title.list`)}
          path=""
          customBackItem={<div />}
          topbarContent={
            <>
              <div className="flex">
                <ViewToggleButton id={viewId} />
                <QueryFilter.ButtonOpener
                  i18nextPrefix={i18nextPrefix}
                  onClick={handleComponentVisible}
                  variant={BUTTON_VARIANT.default}
                  isClear
                />
              </div>

              {/* <Button
                variant={BUTTON_VARIANT.submit}
                onlyIcon
                onClick={() => navigate('create/data', { replace: false })}>
                <Button.Icon icon={<PiPlus />} />
              </Button> */}
            </>
          }
        >
          <QueryFilter<IMerchSearch> i18nextPrefix={i18nextPrefix} initialValues={filters}>
            <>
              <QueryFilter.Form<IMerchSearch>
                ref={ref}
                isOpen={isComponentVisible}
                onDebouncedFormChange={(values) => setSearchById(columnsVisibility.tableId, values)}
              >
                <QueryFilter.InputsGrid>
                  <FormInput.Select
                    appBackground
                    name="status"
                    label={t('input.label.status')}
                    options={getSelectOptions(STATUS, 'enum.STATUS')}
                  />
                  <FormInput.Text appBackground name="name" label={t('input.label.name')} />
                  <FormInput.Text
                    appBackground
                    name="EAN"
                    label={t('input.label.ean')}
                    placeholder="1234567890"
                  />
                </QueryFilter.InputsGrid>
              </QueryFilter.Form>
              <QueryFilterLabels scope="merch" isFormOpen={isComponentVisible} />
            </>
          </QueryFilter>
        </TopBar>
      }
    >
      <ListWrapper
        view={view}
        items={data?.pages.flat()}
        isLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
        item={(merch) => (
          <Card
            view={view}
            key={merch.id}
            onClick={() => navigate(`/merch/${merch.id}/data`)}
            image={merch.images.find((image) => image.isMain)?.imagePreviewLink ?? ''}
            header={merch.name}
            description={merch.ean}
            status={<LabeledStatus status={merch.merchStatus} noname />}
          />
        )}
      />
      {/* <Table
        id={columnsVisibility.tableId}
        select={select}
        columns={[
          {
            label: `${t(
              `${i18nextPrefix}.table.${MERCH_COLUMNS[MERCH_COLUMNS.name]}`
            )}`,
            renderCell: item => (
              <TableItem>
                <div className='flex flex-row items-center gap-4'>
                  <TableItem.Image
                    url={item.images?.find(img => img.isMain)?.imagePreviewLink}
                    alt={item.name}
                  />

                  <TableItem.Text text={item.name} />
                </div>
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(MERCH_COLUMNS.name),
          },

          {
            label: t(
              `${i18nextPrefix}.table.${MERCH_COLUMNS[MERCH_COLUMNS.ean]}`
            ),
            renderCell: item => (
              <TableItem>
                <TableItem.Text text={item.ean} />
              </TableItem>
            ),
            hide: columnsVisibility.hide(MERCH_COLUMNS.ean),
            resize: true,
          },
          {
            label: t(
              `${i18nextPrefix}.table.${MERCH_COLUMNS[MERCH_COLUMNS.status]}`
            ),
            renderCell: item => (
              <TableItem justifyPosition='center'>
                <LabeledStatus status={item.merchStatus} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(MERCH_COLUMNS.status),
          },
        ]}
        displayMode
        loading={isLoading || isFetchingNextPage}
        data={tableData}
        onFullyScroll={fetchNextPage}
      /> */}
    </TopbarPage>
  );
};

export default Merch;
